package er.prateek.crossly.share

import er.prateek.crossly.content.ContentRepository
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.Puzzle
import er.prateek.crossly.persistence.IncomingChallengeStore
import er.prateek.crossly.persistence.OutboundShareStore
import er.prateek.crossly.persistence.ProfileStore
import er.prateek.crossly.persistence.markReferrerConsumed
import er.prateek.crossly.persistence.setStatus
import er.prateek.crossly.persistence.upsertChallenge
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.firebase.AnalyticsLogger
import er.prateek.crossly.firebase.NoOpAnalyticsLogger
import kotlin.time.Clock

data class ChallengeNav(
    val puzzleId: String,
    val challengerName: String,
    val challengerTimeMs: Long = 0,
    /** Set when the challenge is a riddle set: the level and seed to replay. */
    val riddle: RiddleChallengeRef? = null,
    val challengerScore: Int = 0,
)

sealed class ShareOverlay {
    data object None : ShareOverlay()
    data object NamePrompt : ShareOverlay()
    data class OwnChallenge(val url: String) : ShareOverlay()
    data class OwnAppShare(val url: String) : ShareOverlay()
    data class OwnResult(val url: String) : ShareOverlay()
    data class FriendInvite(val name: String) : ShareOverlay()
    data class ChallengeOffer(
        val challengerName: String,
        val puzzleId: String,
        val puzzleLabel: String,
        val bestTimeMs: Long = 0,
        val bestScore: Int = 0,
    ) : ShareOverlay()
    data class ChallengeContinue(
        val challengerName: String,
        val puzzleId: String,
        val puzzleLabel: String,
        val bestTimeMs: Long = 0,
        val bestScore: Int = 0,
    ) : ShareOverlay()
    data class PuzzleMissing(val challengerName: String) : ShareOverlay()
    data class FriendResult(
        val friendName: String,
        val won: Boolean,
        val marginMs: Long,
        val friendTimeMs: Long,
    ) : ShareOverlay()
}

data class ShareUiState(
    val overlay: ShareOverlay = ShareOverlay.None,
    val openPuzzle: ChallengeNav? = null,
    val openLevels: Boolean = false,
)

class ShareManager(
    private val profile: ProfileStore,
    private val outbound: OutboundShareStore,
    private val incoming: IncomingChallengeStore,
    private val content: ContentRepository,
    private val analytics: AnalyticsLogger = NoOpAnalyticsLogger,
    private val clock: () -> Long = { Clock.System.now().toEpochMilliseconds() },
    /** Live `modes.riddles.enabled`: a riddle challenge can't be opened while the mode is off. */
    private val riddlesEnabled: () -> Boolean = { true },
) {
    private val _ui = MutableStateFlow(ShareUiState())
    val ui: StateFlow<ShareUiState> = _ui.asStateFlow()

    /** After Later / Accept this process, don't re-prompt until a cold start. */
    private var skipInboundPromptThisSession = false
    private var started = false
    private var cachedLabel: String? = null
    private var cachedLabelPuzzleId: String? = null

    fun playerName(): String = profile.playerName()

    fun hasPlayerName(): Boolean = playerName().isNotBlank()

    fun savePlayerName(raw: String): String {
        val name = ShareCodec.sanitizeName(raw)
        profile.setPlayerName(name)
        profile.setNamePromptDone()
        return name
    }

    fun skipNamePrompt() {
        profile.setNamePromptDone()
        refreshOverlay()
    }

    fun onNameSaved() {
        profile.setNamePromptDone()
        refreshOverlay()
    }

    fun onColdStart() {
        refreshOverlay()
        if (started) return
        started = true
        if (!incoming.load().referrerConsumed) {
            startInstallReferrerCapture { referrer ->
                incoming.markReferrerConsumed()
                if (!referrer.isNullOrBlank()) {
                    handlePlayReferrer(referrer)
                }
            }
        }
    }

    fun handleIncomingUrl(url: String, event: String = AnalyticsEvents.SHARE_APP_OPEN) {
        val parsed = ShareCodec.parseShareUrl(url) ?: return
        ShareFunnel.log(analytics, event, parsed.first)
        ingest(parsed.first, parsed.second)
    }

    fun handlePlayReferrer(referrer: String) {
        val parsed = ShareCodec.parsePlayReferrer(referrer) ?: return
        ShareFunnel.log(analytics, AnalyticsEvents.SHARE_INSTALL_OPEN, parsed.first)
        ingest(parsed.first, parsed.second)
    }

    fun dismissOverlay() {
        when (_ui.value.overlay) {
            is ShareOverlay.ChallengeOffer -> later()
            is ShareOverlay.ChallengeContinue -> {
                skipInboundPromptThisSession = true
                _ui.update { it.copy(overlay = ShareOverlay.None) }
            }
            else -> _ui.update { it.copy(overlay = ShareOverlay.None) }
        }
    }

    fun later() {
        (_ui.value.overlay as? ShareOverlay.ChallengeOffer)?.let { offer ->
            analytics.logEvent(
                AnalyticsEvents.CHALLENGE_LATER,
                mapOf("puzzle_id" to offer.puzzleId, "challenger_name" to offer.challengerName),
            )
        }
        skipInboundPromptThisSession = true
        _ui.update { it.copy(overlay = ShareOverlay.None) }
    }

    suspend fun acceptChallenge() {
        val challenge = incoming.load().challenge ?: return
        val riddle = RiddleChallengeRef.parse(challenge.puzzleId)
        val playable = if (riddle != null) riddlesEnabled() else content.puzzleById(challenge.puzzleId) != null
        if (!playable) {
            _ui.update { it.copy(overlay = ShareOverlay.PuzzleMissing(challenge.challengerName)) }
            return
        }
        incoming.setStatus(IncomingChallengeStatus.ACCEPTED)
        skipInboundPromptThisSession = true
        val payload = challenge.toPayload()
        ShareFunnel.log(analytics, AnalyticsEvents.SHARE_CHALLENGE_ACCEPT, payload)
        if (isWebRuntime()) {
            ShareFunnel.log(analytics, AnalyticsEvents.SHARE_WEB_PLAY, payload)
        }
        _ui.update {
            it.copy(
                overlay = ShareOverlay.None,
                openPuzzle = ChallengeNav(
                    challenge.puzzleId,
                    challenge.challengerName,
                    challenge.bestTimeMs,
                    riddle = riddle,
                    challengerScore = challenge.bestScore,
                ),
            )
        }
    }

    suspend fun continueChallenge() = acceptChallenge()

    fun startFresh() {
        val challenge = incoming.load().challenge
        analytics.logEvent(
            AnalyticsEvents.CHALLENGE_DECLINED,
            mapOf(
                "puzzle_id" to (challenge?.puzzleId ?: ""),
                "challenger_name" to (challenge?.challengerName ?: ""),
            ),
        )
        incoming.setStatus(IncomingChallengeStatus.DECLINED)
        skipInboundPromptThisSession = true
        _ui.update { it.copy(overlay = ShareOverlay.None) }
    }

    fun dontAskAgain() {
        val challenge = incoming.load().challenge
        analytics.logEvent(
            AnalyticsEvents.CHALLENGE_DONT_ASK,
            mapOf(
                "puzzle_id" to (challenge?.puzzleId ?: ""),
                "challenger_name" to (challenge?.challengerName ?: ""),
            ),
        )
        incoming.setStatus(IncomingChallengeStatus.DONT_ASK)
        skipInboundPromptThisSession = true
        _ui.update { it.copy(overlay = ShareOverlay.None) }
    }

    fun clearOpenPuzzle() {
        _ui.update { it.copy(openPuzzle = null) }
    }

    fun clearOpenLevels() {
        _ui.update { it.copy(openLevels = false) }
    }

    fun playAndChallengeMore() {
        skipInboundPromptThisSession = true
        _ui.update { it.copy(overlay = ShareOverlay.None, openLevels = true) }
    }

    fun markCompletedIfMatching(puzzleId: String) {
        val current = incoming.load().challenge ?: return
        if (current.puzzleId == puzzleId && current.status == IncomingChallengeStatus.ACCEPTED) {
            incoming.setStatus(IncomingChallengeStatus.COMPLETED)
            ShareFunnel.log(analytics, AnalyticsEvents.SHARE_CHALLENGE_COMPLETE, current.toPayload())
        }
    }

    fun shareApp(name: String) = shareWithAppLink(name, "Play Crossly with me")

    /**
     * Shares a brag (a streak, a badge) carrying the same tracked app-share link as [shareApp], so
     * installs it drives are attributed through the existing referral funnel.
     */
    fun shareWithAppLink(name: String, message: String) {
        val (token, url) = ShareCodec.appShareUrl(name, profile.installId())
        val payload = ShareCodec.decode(token)
        outbound.add(
            OutboundShare(
                token = token,
                puzzleId = "",
                createdAtEpochMs = clock(),
                shareId = payload?.shareId.orEmpty(),
            ),
        )
        payload?.let { ShareFunnel.log(analytics, AnalyticsEvents.SHARE_CREATED, it) }
        shareText("$message\n$url")
    }

    fun shareChallenge(name: String, puzzleId: String, puzzleLabel: String, timeMs: Long = 0, score: Int = 0) {
        val (token, url) = ShareCodec.challengeUrl(name, puzzleId, timeMs, profile.installId(), score)
        val payload = ShareCodec.decode(token)
        outbound.add(
            OutboundShare(
                token = token,
                puzzleId = puzzleId,
                createdAtEpochMs = clock(),
                shareId = payload?.shareId.orEmpty(),
            ),
        )
        payload?.let { ShareFunnel.log(analytics, AnalyticsEvents.SHARE_CREATED, it, mapOf("puzzle_label" to puzzleLabel)) }
        val message = if (score > 0 && timeMs > 0) {
            "I cleared $puzzleLabel on Crossly in ${formatSpokenDuration(timeMs)} with $score correct. Beat that!\n$url"
        } else {
            "I challenge you to $puzzleLabel on Crossly\n$url"
        }
        shareText(message)
    }

    fun shareWinWithReferrer(
        name: String,
        referrerName: String,
        puzzleId: String,
        puzzleLabel: String,
        myTimeMs: Long,
        theirTimeMs: Long,
    ) {
        val (token, url) = ShareCodec.resultUrl(name, puzzleId, myTimeMs, theirTimeMs, profile.installId())
        val payload = ShareCodec.decode(token)
        outbound.add(
            OutboundShare(
                token = token,
                puzzleId = puzzleId,
                createdAtEpochMs = clock(),
                shareId = payload?.shareId.orEmpty(),
            ),
        )
        val margin = formatSpokenDuration(timeMarginMs(myTimeMs, theirTimeMs))
        payload?.let {
            ShareFunnel.log(
                analytics,
                AnalyticsEvents.SHARE_CREATED,
                it,
                mapOf(
                    "puzzle_label" to puzzleLabel,
                    "won_vs" to referrerName,
                    "margin" to margin,
                ),
            )
        }
        shareText("I won your challenge by $margin. See my results here\n$url")
    }

    fun shareOwnUrl(url: String, challenge: Boolean) {
        val text = if (challenge) {
            "Share this link with a friend to challenge them on Crossly\n$url"
        } else {
            "Share this link with a friend to invite them to Crossly\n$url"
        }
        shareText(text)
    }

    fun shareOwnResultUrl(url: String) {
        shareText("See my Crossly results\n$url")
    }

    suspend fun puzzleLabel(puzzleId: String): String? {
        RiddleChallengeRef.parse(puzzleId)?.let { return riddleLabel(it.levelId) }
        val puzzle = content.puzzleById(puzzleId) ?: return null
        val inTier = content.puzzlesByTier(puzzle.difficulty)
        return puzzleLevelLabel(tierDisplayName(puzzle.difficulty), inTier.indexOfFirst { it.id == puzzle.id })
    }

    /** `Math Riddles Easy Set 3` from a level id like `riddle-easy-003`. */
    private fun riddleLabel(levelId: String): String {
        val tier = runCatching { DifficultyTier.valueOf(levelId.split("-")[1].uppercase()) }.getOrNull()
        val index = levelId.substringAfterLast("-").toIntOrNull()
        return buildString {
            append("Math Riddles")
            if (tier != null) append(" ${tierDisplayName(tier)}")
            if (index != null) append(" Set $index")
        }
    }

    fun refreshOverlay() {
        when (_ui.value.overlay) {
            is ShareOverlay.OwnChallenge,
            is ShareOverlay.OwnAppShare,
            is ShareOverlay.OwnResult,
            is ShareOverlay.FriendInvite,
            is ShareOverlay.FriendResult,
            is ShareOverlay.PuzzleMissing,
            -> return
            else -> Unit
        }
        if (!profile.namePromptDone() && profile.playerName().isBlank()) {
            _ui.update { it.copy(overlay = ShareOverlay.NamePrompt) }
            return
        }
        if (skipInboundPromptThisSession) {
            if (_ui.value.overlay is ShareOverlay.NamePrompt) {
                _ui.update { it.copy(overlay = ShareOverlay.None) }
            }
            return
        }
        val challenge = incoming.load().challenge
        val overlay = when (challenge?.status) {
            IncomingChallengeStatus.OFFERED ->
                ShareOverlay.ChallengeOffer(
                    challengerName = challenge.challengerName,
                    puzzleId = challenge.puzzleId,
                    puzzleLabel = labelFor(challenge.puzzleId),
                    bestTimeMs = challenge.bestTimeMs,
                    bestScore = challenge.bestScore,
                )
            IncomingChallengeStatus.ACCEPTED ->
                ShareOverlay.ChallengeContinue(
                    challengerName = challenge.challengerName,
                    puzzleId = challenge.puzzleId,
                    puzzleLabel = labelFor(challenge.puzzleId),
                    bestTimeMs = challenge.bestTimeMs,
                    bestScore = challenge.bestScore,
                )
            else -> ShareOverlay.None
        }
        // Guarded on an actual transition into the overlay, not every refreshOverlay() call
        // (which fires on recomposition/cold-start far more often than the overlay changes) --
        // otherwise this event would spam far out of proportion to how often it's really shown.
        if (overlay != _ui.value.overlay &&
            (overlay is ShareOverlay.ChallengeOffer || overlay is ShareOverlay.ChallengeContinue)
        ) {
            val (puzzleId, challengerName, isContinue) = when (overlay) {
                is ShareOverlay.ChallengeOffer -> Triple(overlay.puzzleId, overlay.challengerName, false)
                is ShareOverlay.ChallengeContinue -> Triple(overlay.puzzleId, overlay.challengerName, true)
            }
            analytics.logEvent(
                AnalyticsEvents.CHALLENGE_OFFER_SHOWN,
                mapOf("puzzle_id" to puzzleId, "challenger_name" to challengerName, "is_continue" to isContinue),
            )
        }
        _ui.update { it.copy(overlay = overlay) }
    }

    suspend fun refreshOverlayWithLabels() {
        val challenge = incoming.load().challenge
        if (challenge != null &&
            (challenge.status == IncomingChallengeStatus.OFFERED ||
                challenge.status == IncomingChallengeStatus.ACCEPTED)
        ) {
            cachedLabel = puzzleLabel(challenge.puzzleId) ?: challenge.puzzleId
            cachedLabelPuzzleId = challenge.puzzleId
        }
        refreshOverlay()
    }

    /** Only trusts [cachedLabel] when it was resolved for this exact puzzle -- otherwise a
     *  label left over from a previous challenge (or none yet) would flash before the async
     *  fetch in [refreshOverlayWithLabels] catches up. */
    private fun labelFor(puzzleId: String): String =
        cachedLabel.takeIf { cachedLabelPuzzleId == puzzleId } ?: puzzleId

    private fun ingest(payload: SharePayload, token: String) {
        if (ShareCodec.isOwnLink(payload, token, profile.installId(), outbound.all().map { it.token }.toSet())) {
            val url = ShareCodec.urlFor(token, payload.kind)
            _ui.update {
                it.copy(
                    overlay = when (payload.kind) {
                        SharePayload.Kind.APP_SHARE -> ShareOverlay.OwnAppShare(url)
                        SharePayload.Kind.CHALLENGE -> ShareOverlay.OwnChallenge(url)
                        SharePayload.Kind.RESULT -> ShareOverlay.OwnResult(url)
                    },
                )
            }
            return
        }
        when (payload.kind) {
            SharePayload.Kind.APP_SHARE -> {
                _ui.update { it.copy(overlay = ShareOverlay.FriendInvite(payload.name)) }
            }
            SharePayload.Kind.CHALLENGE -> {
                skipInboundPromptThisSession = false
                incoming.upsertChallenge(
                    IncomingChallenge(
                        token = token,
                        challengerName = payload.name,
                        puzzleId = payload.puzzleId,
                        status = IncomingChallengeStatus.OFFERED,
                        shareId = payload.shareId,
                        origin = payload.origin,
                        bestTimeMs = payload.timeMs,
                        bestScore = payload.score,
                    ),
                )
                refreshOverlay()
            }
            SharePayload.Kind.RESULT -> {
                val won = challengeWon(payload.timeMs, payload.opponentTimeMs)
                _ui.update {
                    it.copy(
                        overlay = ShareOverlay.FriendResult(
                            friendName = payload.name,
                            won = won,
                            marginMs = timeMarginMs(payload.timeMs, payload.opponentTimeMs),
                            friendTimeMs = payload.timeMs,
                        ),
                    )
                }
            }
        }
    }
}

fun tierDisplayName(tier: DifficultyTier): String = when (tier) {
    DifficultyTier.EASY -> "Easy"
    DifficultyTier.MEDIUM -> "Medium"
    DifficultyTier.HARD -> "Hard"
    DifficultyTier.EXPERT -> "Expert"
}

fun IncomingChallenge.toPayload(): SharePayload = SharePayload(
    kind = SharePayload.Kind.CHALLENGE,
    name = challengerName,
    puzzleId = puzzleId,
    shareId = shareId,
    origin = origin,
    timeMs = bestTimeMs,
    score = bestScore,
)

suspend fun resolvePuzzleLabel(
    content: ContentRepository,
    puzzleId: String,
): Pair<Puzzle, String>? {
    val puzzle = content.puzzleById(puzzleId) ?: return null
    val inTier = content.puzzlesByTier(puzzle.difficulty)
    val label = puzzleLevelLabel(tierDisplayName(puzzle.difficulty), inTier.indexOfFirst { it.id == puzzle.id })
    return puzzle to label
}
